import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { isDemoAccount } from '../lib/auth';

export function ActiveStatusCheck() {
  const navigate = useNavigate();
  const modalRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const checkSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          // Skip check for demo accounts
          if (isDemoAccount(session.user.email)) {
            return;
          }
          
          const { data: profile, error } = await supabase
            .from('profiles')
            .select('last_active')
            .eq('id', session.user.id)
            .single();

          if (error) {
            console.error('Error fetching profile:', error);
            return;
          }

          const lastActive = profile?.last_active ? new Date(profile.last_active) : null;
          const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);

          if (!lastActive || lastActive < oneDayAgo) {
            showActivePrompt();
          }
        }
      } catch (error) {
        console.error('Error checking session:', error);
      }
    };

    checkSession();

    // Cleanup function to remove modal if component unmounts
    return () => {
      if (modalRef.current && document.body.contains(modalRef.current)) {
        document.body.removeChild(modalRef.current);
      }
    };
  }, []);

  const showActivePrompt = () => {
    // Remove existing modal if it exists
    if (modalRef.current && document.body.contains(modalRef.current)) {
      document.body.removeChild(modalRef.current);
    }

    const modal = document.createElement('div');
    modalRef.current = modal;
    
    modal.innerHTML = `
      <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div class="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
          <h2 class="text-xl font-bold mb-4">Still Looking?</h2>
          <p class="mb-6">Are you still actively looking for housing or roommates?</p>
          <div class="flex justify-end space-x-4">
            <button id="not-active" class="px-4 py-2 text-gray-600 hover:text-gray-800">
              No, Not Currently
            </button>
            <button id="still-active" class="px-4 py-2 bg-primary-400 text-white rounded-md hover:bg-primary-500">
              Yes, Still Looking
            </button>
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(modal);

    const updateStatus = async (isActive: boolean) => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          const { error } = await supabase
            .from('profiles')
            .update({ 
              is_active: isActive,
              last_active: new Date().toISOString()
            })
            .eq('id', session.user.id);

          if (error) {
            console.error('Error updating status:', error);
            return;
          }
        }

        // Safely remove modal
        if (modalRef.current && document.body.contains(modalRef.current)) {
          document.body.removeChild(modalRef.current);
          modalRef.current = null;
        }
        
        if (!isActive) {
          navigate('/profile');
        }
      } catch (error) {
        console.error('Error in updateStatus:', error);
      }
    };

    modal.querySelector('#still-active')?.addEventListener('click', () => updateStatus(true));
    modal.querySelector('#not-active')?.addEventListener('click', () => updateStatus(false));
  };

  return null;
}